import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API_URL = "https://backend.aktcorp.com/events";

export const EditEvent = () => {
  const [events, setEvents] = useState([]);
  const [editData, setEditData] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPage, setShowPage] = useState(false);

  const token = localStorage.getItem("authToken");

  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`, // ✅ JWT token header
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPage(true);
      fetchEvents();
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

const fetchEvents = async () => {
  try {
    const response = await axios.get(API_URL, { headers });
    // console.log(response.data); // Debug: Check API response
    setEvents(response.data.data); // ✅ THIS is the actual array of events
  } catch (error) {
    console.error("Error fetching events:", error);
    setEvents([]); // Prevent errors if fetch fails
  }
};

  if (!showPage) {
    return (
      <div className="flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
          className="w-24 h-24 border-4 border-t-transparent border-blue-600 rounded-full mx-auto mt-32"
        ></motion.div>
      </div>
    );
  }

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    let imageBase64 = null;

    // Convert selected image to Base64
    if (imageFile) {
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      await new Promise((resolve) => {
        reader.onloadend = () => {
          imageBase64 = reader.result.split(",")[1];
          resolve();
        };
      });
    }

    const payload = {
      title: editData.title,
      description: editData.description,
      imageBase64: imageBase64 || undefined, // If no new image, keep existing one
    };

    try {
      await axios.put(`${API_URL}/${editData.id}`, payload, { headers });

      setTimeout(() => {
        setLoading(false);
        setSuccessMessage("✅ Event updated successfully!");

        setTimeout(() => {
          setSuccessMessage("");
          setEditData(null);
          setImageFile(null);
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }, 3000);
      }, 2000);

      fetchEvents();
    } catch (error) {
      setLoading(false);
      setErrorMessage("❌ Failed to update event.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow-md rounded-lg mb-10">
      <motion.h2
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-3xl font-bold text-gray-900 text-center mb-4"
      >
        Edit Events
      </motion.h2>

<ul>
  {events.map((item) => (
    <li key={item.id} className="border-b py-3 flex justify-between items-center">
      <div className="flex justify-center">
        {item.image && (
          <img
            src={item.image}
            alt="Event"
            className="w-14 h-14 object-cover mr-6 rounded-md"
          />
        )}
        <div className="max-w-2xl">
          <h3 className="text-lg font-semibold">{item.title}</h3>
          <p className="text-gray-600">{item.description}</p>
        </div>
      </div>
      <button onClick={() => setEditData(item)} className="text-blue-500 text-lg font-semibold">✏️ Edit</button>
    </li>
  ))}
</ul>

      {editData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <motion.h2
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-gray-900 text-center mb-4"
            >
              Edit Event
            </motion.h2>
            <form onSubmit={handleUpdate}>
              <p className="font-medium">Title:</p>
              <input
                type="text"
                className="border w-full p-2 mb-2 rounded-md"
                value={editData.title}
                onChange={(e) =>
                  setEditData({ ...editData, title: e.target.value })
                }
              />
              <p className="font-medium">Description:</p>
              <textarea
                className="border w-full p-2 mb-2 rounded-md min-h-20"
                value={editData.description}
                onChange={(e) =>
                  setEditData({ ...editData, description: e.target.value })
                }
              />

              <p className="font-medium">Update Image (Optional):</p>
              <input
                type="file"
                accept="image/*"
                className="w-full mb-4 border border-gray-300 rounded-md p-2"
                onChange={(e) => setImageFile(e.target.files[0])}
              />

              <div className="flex justify-between mt-4">
                {!loading ? (
                  <>
                    <button
                      type="submit"
                      className="bg-blue-500 text-white px-4 py-2 rounded-md"
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditData(null)}
                      className="bg-gray-500 text-white px-4 py-2 rounded-md"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    className="w-8 h-8 border-4 border-t-transparent border-blue-600 rounded-full mx-auto mt-6"
                  ></motion.div>
                )}
              </div>
              {successMessage && (
                <div className="text-green-500 text-center">{successMessage}</div>
              )}
              {errorMessage && (
                <div className="text-red-500 text-center">{errorMessage}</div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditEvent;
